/**
 * Client half of the tracking API (PRD §4.6, §6.4, §7.2): query keys, fetchers, mutations.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { AdminWeekNote } from "@/lib/admin";
import { api, saveBlob } from "@/lib/api";
import type { ProjectSummary } from "@/shared/schemas/tenancy";
import type {
  CalendarMonth,
  LiveOverview,
  LiveStatus,
  MonthLockSummary,
  WeekSaveInput,
} from "@/shared/schemas/tracking";

export const trackingKeys = {
  myProjects: () => ["tracking", "me", "projects"] as const,
  calendar: (userId: string, projectId: string, year: number, month: number) =>
    ["tracking", "calendar", userId, projectId, { year, month }] as const,
  live: (projectId: string, year: number) => ["tracking", "live", projectId, { year }] as const,
  weekNotes: (projectId: string, year: number) =>
    ["tracking", "week-notes", projectId, { year }] as const,
  weekAttachments: (userId: string, projectId: string, year: number, isoWeek: number) =>
    ["tracking", "week-attachments", userId, projectId, { year, isoWeek }] as const,
  locks: (projectId: string, year?: number, history?: boolean) =>
    ["tracking", "locks", projectId, { year, history: history ?? false }] as const,
  liveStatus: () => ["tracking", "live-status"] as const,
};

export interface WeekAttachmentItem {
  id: string;
  filename: string;
  byteSize: number;
  mimeType: string;
  sha256: string;
  createdAt: string;
}

function touch(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: ["tracking"] });
  void client.invalidateQueries({ queryKey: ["projects"] });
  void client.invalidateQueries({ queryKey: ["companies"] });
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

export function useMyProjects() {
  return useQuery({
    queryKey: trackingKeys.myProjects(),
    queryFn: () => api.get<{ projects: ProjectSummary[] }>("/me/projects"),
    select: (data) => data.projects,
  });
}

export function useCalendar(input: {
  userId: string | undefined;
  projectId: string | undefined;
  year: number;
  month: number;
  /** Own calendar uses `/me/...`; admin edit uses `/users/:id/...`. */
  asAdmin?: boolean;
}) {
  const own = !input.asAdmin;
  return useQuery({
    queryKey: trackingKeys.calendar(
      input.userId ?? "none",
      input.projectId ?? "none",
      input.year,
      input.month,
    ),
    enabled: Boolean(input.userId && input.projectId),
    queryFn: () => {
      const path = own
        ? `/me/projects/${input.projectId}/calendar`
        : `/users/${input.userId}/projects/${input.projectId}/calendar`;
      return api.get<{ calendar: CalendarMonth }>(
        `${path}${query({ year: input.year, month: input.month })}`,
      );
    },
    select: (data) => data.calendar,
  });
}

export function useSaveWeek(input: { userId: string; projectId: string; asAdmin?: boolean }) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: WeekSaveInput) => {
      const path = input.asAdmin
        ? `/users/${input.userId}/projects/${input.projectId}/weeks`
        : `/me/projects/${input.projectId}/weeks`;
      return api.post<void>(path, body);
    },
    onSuccess: () => {
      // Shared day_records: drop other projects' calendars so navigating there
      // does not paint stale hours / miss absence until a full reload.
      client.removeQueries({
        queryKey: ["tracking", "calendar"],
        predicate: (query) => query.queryKey[3] !== input.projectId,
      });
      touch(client);
    },
  });
}

export function useLiveOverview(projectId: string | undefined, year: number) {
  return useQuery({
    queryKey: trackingKeys.live(projectId ?? "none", year),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ overview: LiveOverview }>(`/projects/${projectId}/live${query({ year })}`),
    select: (data) => data.overview,
  });
}

export function useProjectWeekNotes(projectId: string | undefined, year: number) {
  return useQuery({
    queryKey: trackingKeys.weekNotes(projectId ?? "none", year),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ entries: AdminWeekNote[] }>(`/projects/${projectId}/week-notes${query({ year })}`),
    select: (data) => data.entries,
  });
}

export function useProjectLocks(
  projectId: string | undefined,
  options: { year?: number; history?: boolean } = {},
) {
  const { year, history } = options;
  return useQuery({
    queryKey: trackingKeys.locks(projectId ?? "none", year, history),
    enabled: Boolean(projectId),
    queryFn: () =>
      api.get<{ locks: MonthLockSummary[] }>(
        `/projects/${projectId}/locks${query({
          year,
          history: history ? 1 : undefined,
        })}`,
      ),
    select: (data) => data.locks,
  });
}

export function useLockMonth(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { year: number; fromMonth: number; toMonth: number }) =>
      api.post<{ lock: MonthLockSummary }>(`/projects/${projectId}/locks`, input),
    onSuccess: () => touch(client),
  });
}

export function useUnlockMonth(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { lockId: string; reason: string }) =>
      api.post<{ lock: MonthLockSummary }>(`/projects/${projectId}/locks/${input.lockId}/unlock`, {
        reason: input.reason,
      }),
    onSuccess: () => touch(client),
  });
}

export function useLiveStatus(enabled = true) {
  return useQuery({
    queryKey: trackingKeys.liveStatus(),
    enabled,
    queryFn: () => api.get<{ status: LiveStatus }>("/dashboard/live-status"),
    select: (data) => data.status,
    refetchInterval: 60_000,
  });
}

/** Dashboard live-status Reminder — company-scoped (G-Admin and Company Admin). */
export function useSendProjectReminders(projectId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input?: { userIds?: string[]; locale?: "de" | "en" }) =>
      api.post<{ sent: number; results: { userId: string; status: string }[] }>(
        `/projects/${projectId}/reminders/send`,
        input ?? {},
      ),
    onSuccess: () => touch(client),
  });
}

export function useDownloadStundenzettel(projectId: string) {
  return useMutation({
    mutationFn: async (input: { year: number; userId?: string }) => {
      const qs = query({ year: input.year, userId: input.userId });
      const { blob, filename } = await api.download(
        `/projects/${projectId}/exports/stundenzettel${qs}`,
      );
      saveBlob(blob, filename);
      return filename;
    },
  });
}

export function useLogExport() {
  return useMutation({
    mutationFn: (body: {
      type: "export_year_matrix";
      projectId?: string | null;
      year?: number;
      format?: string;
    }) => api.post<void>("/exports/log", body),
  });
}

function weekAttachmentPath(
  input: { userId: string; projectId: string; year: number; isoWeek: number; asAdmin?: boolean },
  suffix = "",
): string {
  const base = input.asAdmin
    ? `/users/${input.userId}/projects/${input.projectId}`
    : `/me/projects/${input.projectId}`;
  if (suffix) return `${base}/attachments/${suffix}`;
  return `${base}/weeks/${input.year}/${input.isoWeek}/attachments`;
}

export function useWeekAttachments(input: {
  userId: string;
  projectId: string;
  year: number;
  isoWeek: number;
  asAdmin?: boolean;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: trackingKeys.weekAttachments(
      input.userId,
      input.projectId,
      input.year,
      input.isoWeek,
    ),
    enabled: input.enabled ?? true,
    queryFn: () => api.get<{ attachments: WeekAttachmentItem[] }>(weekAttachmentPath(input)),
    select: (data) => data.attachments,
  });
}

export function useUploadWeekAttachment(input: {
  userId: string;
  projectId: string;
  year: number;
  isoWeek: number;
  asAdmin?: boolean;
}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (file: File) =>
      api.upload<{ attachment: WeekAttachmentItem }>(weekAttachmentPath(input), file, {
        filename: file.name,
      }),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: trackingKeys.weekAttachments(
          input.userId,
          input.projectId,
          input.year,
          input.isoWeek,
        ),
      });
      void client.invalidateQueries({
        queryKey: trackingKeys.weekNotes(input.projectId, input.year),
      });
      void client.invalidateQueries({ queryKey: trackingKeys.live(input.projectId, input.year) });
    },
  });
}

export function useDeleteWeekAttachment(input: {
  userId: string;
  projectId: string;
  year: number;
  isoWeek: number;
  asAdmin?: boolean;
}) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { attachmentId: string; changeReason?: string }) =>
      api.delete<void>(
        weekAttachmentPath(input, body.attachmentId),
        body.changeReason ? { changeReason: body.changeReason } : undefined,
      ),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: trackingKeys.weekAttachments(
          input.userId,
          input.projectId,
          input.year,
          input.isoWeek,
        ),
      });
      void client.invalidateQueries({
        queryKey: trackingKeys.weekNotes(input.projectId, input.year),
      });
      void client.invalidateQueries({ queryKey: trackingKeys.live(input.projectId, input.year) });
    },
  });
}

export function useDownloadWeekAttachment(input: {
  userId: string;
  projectId: string;
  asAdmin?: boolean;
}) {
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const base = input.asAdmin
        ? `/users/${input.userId}/projects/${input.projectId}`
        : `/me/projects/${input.projectId}`;
      const { blob, filename } = await api.download(`${base}/attachments/${attachmentId}`);
      saveBlob(blob, filename);
      return filename;
    },
  });
}

export function useDownloadAttachmentsZip(projectId: string, year: number) {
  return useMutation({
    mutationFn: async () => {
      const { blob, filename } = await api.download(
        `/projects/${projectId}/exports/attachments.zip?year=${year}`,
      );
      saveBlob(blob, filename);
      return filename;
    },
  });
}
